import React from 'react';
import { connect } from 'react-redux';
import { Text, Tooltip as UIKitTooltip } from '@beef/ui-kit';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';

import { BundleFrame, BundleWrapper, ContentWrap, Image } from 'bricks';
import { FancyNumbers } from 'pages/ProductDetails/views/Simple/components/index';
import { isScreenBelow800, isScreenBelow1280 } from 'store/common/settings/breakpoint/selectors';
import {
  PRODUCT_KIND_ACCESSORY,
  PRODUCT_KIND_DEVICE,
  PRODUCT_KIND_FANCYNUMBER,
  PRODUCT_KIND_TARIFF,
  TARIFF_DEFAULT_IMG,
  TARIFF_ESIM_IMG,
} from 'store/ProductDetails/bundles/constants';
import { FOLLOW_PROMO_SCROLL_ID } from 'store/ProductDetails/common/constants';
import { Bundle, getHasEsim } from 'store/ProductDetails/bundles/selectors';
import { getLeasingBundleAvailability, selectIsBundleActive } from 'store/ProductDetails/selectors';
import commonStyles from 'styles/common.pcss';
import { formatPriceExtended } from 'utils/formatting';

import BundleBuyButton from '../BundleBuyButton';
import styles from './styles.pcss';

const cx = classNames.bind({ ...commonStyles, ...styles });

/**
 * Common root for bundle items - including products, tariffs and fancy numbers
 */
const BundleItem = ({ children, isMobile }) => (
  <div
    className={cx('product', {
      'product--mobile': isMobile,
    })}
  >
    {children}
  </div>
);

BundleItem.propTypes = {
  children: PropTypes.node,
  isMobile: PropTypes.bool,
};

const BundleProduct = ({
  hasEsim,
  imageUrl,
  isMobile,
  oldPrice,
  price,
  slug,
  title,
  tooltip,
  isBundleActive,
}) => (
  <BundleItem isMobile={isMobile}>
    <div
      className={cx('image-wrapper', {
        'image-wrapper--mobile': isMobile,
      })}
    >
      {!imageUrl && slug === PRODUCT_KIND_TARIFF ?
        <Image
          alt={title}
          className={cx('image')}
          pictureClassName={cx('picture')}
          src={hasEsim ? TARIFF_ESIM_IMG : TARIFF_DEFAULT_IMG}
        />
      : <Image
          alt={title}
          className={cx('image')}
          pictureClassName={cx('picture')}
          src={slug === PRODUCT_KIND_TARIFF && hasEsim ? TARIFF_ESIM_IMG : imageUrl}
        />
      }
    </div>
    <div className={cx('info-wrapper')}>
      <div className={cx('price-wrapper')}>
        <Text size="size3-b">{formatPriceExtended(price)}</Text>
        {!isBundleActive && oldPrice && price !== oldPrice && (
          <div className={cx('old-price-wrapper')}>
            <Text color="grey50" crossOutColor="scarlet" isCrossedOut size="size6-r">
              {formatPriceExtended(oldPrice)}
            </Text>
          </div>
        )}
      </div>
      <div className={cx('title')}>
        {tooltip ?
          <UIKitTooltip message={tooltip}>
            <Text color="grey50" size="size5-r-s">
              {' '}
              {title}
            </Text>
          </UIKitTooltip>
        : <Text color="grey50" size="size5-r-s">
            {' '}
            {title}
          </Text>
        }
      </div>
    </div>
  </BundleItem>
);

BundleProduct.propTypes = {
  hasEsim: PropTypes.bool || null,
  isBundleActive: PropTypes.bool,
  imageUrl: PropTypes.string,
  isMobile: PropTypes.bool,
  oldPrice: PropTypes.number,
  price: PropTypes.number,
  slug: PropTypes.oneOf([
    PRODUCT_KIND_DEVICE,
    PRODUCT_KIND_TARIFF,
    PRODUCT_KIND_FANCYNUMBER,
    PRODUCT_KIND_ACCESSORY,
  ]),
  title: PropTypes.string,
  tooltip: PropTypes.string,
};

/**
 * Bundle Item for fancy number
 */
const BundleFancyNumber = () => (
  <BundleItem>
    <FancyNumbers />
  </BundleItem>
);

/**
 * A switch for all BundleItems, based on slug
 * @param slug
 * @param otherProps
 * @returns {*}
 * @constructor
 */
const BundleItemSwitch = ({ slug, ...otherProps }) => {
  if (slug === PRODUCT_KIND_FANCYNUMBER) {
    return <BundleFancyNumber {...otherProps} />;
  }

  return <BundleProduct slug={slug} {...otherProps} />;
};

BundleItemSwitch.propTypes = {
  slug: PropTypes.oneOf([
    PRODUCT_KIND_DEVICE,
    PRODUCT_KIND_TARIFF,
    PRODUCT_KIND_FANCYNUMBER,
    PRODUCT_KIND_ACCESSORY,
  ]),
};

const BundleItemInner = ({
  below1280,
  bundle,
  bundleHasLeasing,
  hasEsim,
  index,
  isMobile,
  isBundleActive,
}) => {
  const getFrameClass = (size) => {
    if (size > 3 && size <= 5 && below1280) {
      return 'small';
    }
    if (size > 5) {
      return 'min';
    }
    return '';
  };

  return (
    <div id={FOLLOW_PROMO_SCROLL_ID} className={cx('wrapper', isMobile && 'mobile')} key={+index}>
      <ContentWrap size={['xl']}>
        <BundleWrapper className={cx('bundle-wrapper', isMobile && 'mobile')} isColumn={isMobile}>
          <div className={cx('bundle-accent', { 'bundle-accent--mobile': isMobile })}>
            <div className={cx('bundle-heading', isMobile && 'mobile')}>
              <Text size="size3-m">С комплектом выгоднее</Text>
            </div>
            <div className={cx('bundle-list', { 'bundle-list--mobile': isMobile })}>
              {bundle.getBundleProducts().map((product, productIndex) => (
                <BundleFrame
                  className={cx(getFrameClass(bundle.getBundleProducts().length))}
                  isMobile={isMobile}
                  key={+productIndex}
                >
                  <BundleItemSwitch
                    bundleHasLeasing={bundleHasLeasing}
                    hasEsim={hasEsim}
                    imageUrl={product.getThumbUrl()}
                    isMobile={isMobile}
                    isBundleActive={isBundleActive}
                    oldPrice={product.getOldPrice()}
                    price={product.getPrice()}
                    slug={product.getSlug()}
                    title={product.getTitle()}
                    tooltip={product.getTooltip()}
                  />
                </BundleFrame>
              ))}
            </div>
          </div>
          {!isMobile && <BundleBuyButton bundle={bundle} />}
        </BundleWrapper>
      </ContentWrap>
    </div>
  );
};

BundleItemInner.propTypes = {
  below1280: PropTypes.bool,
  bundle: PropTypes.instanceOf(Bundle),
  isBundleActive: PropTypes.bool,
  bundleHasLeasing: PropTypes.bool,
  hasEsim: PropTypes.bool,
  index: PropTypes.number,
  isMobile: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  below1280: isScreenBelow1280(state),
  bundleHasLeasing: getLeasingBundleAvailability(state),
  hasEsim: getHasEsim(state),
  isMobile: isScreenBelow800(state),
  isBundleActive: selectIsBundleActive(state),
});

export default connect(mapStateToProps)(BundleItemInner);
